import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Eager-loads a slide's image (drops loading="lazy") so the browser fetches it
 * immediately at its responsive size. Because the slide image is the same
 * image_tag render as the visible gallery, the URL matches and the browser
 * caches it for instant display on swatch select.
 * @param {Element} slide
 */
function eagerLoadSlideImage(slide) {
  const img = slide?.querySelector('img');
  if (img instanceof HTMLImageElement && img.loading === 'lazy') img.loading = 'eager';
}

/**
 * Hides the variant image warning message and placeholder.
 * Shows the slideshow again if it was hidden.
 */
function hideVariantImageWarning() {
  // Remove warning element
  const warningElement = document.querySelector('.variant-image-warning');
  if (warningElement) {
    warningElement.remove();
  }

  // Remove placeholder container
  const placeholderContainer = document.querySelector('.variant-image-placeholder');
  if (placeholderContainer) {
    placeholderContainer.remove();
  }

  // Show slideshow again if it was hidden
  const slideshow = document.querySelector('slideshow-component[style*="display: none"]');
  if (slideshow instanceof HTMLElement) {
    slideshow.style.display = '';
  }
}

/**
 * Shows a warning message when the selected variant has no image.
 * Hides the slideshow and shows an empty placeholder with the warning.
 *
 * @param {HTMLElement} mediaGallery - The media gallery element.
 */
function showVariantImageWarning(mediaGallery) {
  // Remove any existing warning and placeholder first
  hideVariantImageWarning();

  // Find the slideshow component
  const slideshow = mediaGallery?.querySelector('slideshow-component');
  if (!slideshow || !(slideshow instanceof HTMLElement)) return;

  // Hide the slideshow
  const slideshowWrapper = slideshow.closest('.product-media-gallery__slideshow-wrapper') || slideshow.parentElement;
  if (slideshowWrapper && slideshowWrapper instanceof HTMLElement) {
    slideshow.style.display = 'none';

    // Create empty placeholder container
    const existingPlaceholder = mediaGallery.querySelector('.variant-image-placeholder');
    let placeholderContainer = existingPlaceholder instanceof HTMLElement ? existingPlaceholder : null;
    if (!placeholderContainer) {
      placeholderContainer = document.createElement('div');
      placeholderContainer.className = 'variant-image-placeholder';
      placeholderContainer.style.cssText = `
        position: relative;
        width: 100%;
        min-height: 400px;
        background-color: var(--color-background, #fff);
        display: flex;
        align-items: center;
        justify-content: center;
      `;
      
      // Insert placeholder after slideshow wrapper or before slideshow
      const parentElement = slideshowWrapper.parentElement;
      if (parentElement) {
        if (slideshowWrapper.nextSibling) {
          parentElement.insertBefore(placeholderContainer, slideshowWrapper.nextSibling);
        } else {
          parentElement.appendChild(placeholderContainer);
        }
      }
    }

    // Create warning element
    const warningElement = document.createElement('div');
    warningElement.className = 'variant-image-warning';
    warningElement.setAttribute('role', 'alert');
    warningElement.style.cssText = `
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 4px;
      text-align: center;
      font-size: 0.9rem;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 10;
      pointer-events: none;
    `;
    warningElement.textContent = 'İlgili varyasyon görseli henüz eklenmemiştir';

    placeholderContainer.appendChild(warningElement);
  }
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Optimistically switch to the swatch's image immediately on input change,
    // before the section fetch (network round-trip) completes.
    target?.addEventListener('change', this.#handleSwatchChange, { signal });

    // Preload the variant image while hovering so it's ready on click.
    target?.addEventListener('pointerenter', this.#handleSwatchHover, { signal, capture: true });

    // Proactively eager-load the hidden variant slides once idle, so the first
    // click on any swatch — including returning to the first/main image — is instant.
    const rIC = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 400));
    rIC(() => this.#preloadHiddenVariantSlides(), { timeout: 2500 });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Returns the slideshow-slide element for a given media ID (matching its
   * slide-id attribute, including hidden variant slides), or null.
   * @param {string} mediaId
   * @returns {Element | null}
   */
  #findSlideByMediaId(mediaId) {
    return this.querySelector(`slideshow-slide[slide-id="${mediaId}"]`);
  }

  /**
   * Eager-loads the variant's slide image when the user hovers over a swatch,
   * so it is already cached the instant they click.
   * @param {PointerEvent} event
   */
  #handleSwatchHover = (event) => {
    if (event.pointerType !== 'mouse') return;
    const el = /** @type {Element} */ (event.target);
    // The swatch input is hidden inside the label, so find it via the label.
    const input =
      el?.closest('input[data-option-media-id]') ?? el?.closest('label')?.querySelector('input[data-option-media-id]');
    if (!(input instanceof HTMLInputElement)) return;
    const mediaId = input.dataset.optionMediaId;
    if (!mediaId) return;
    const slide = this.#findSlideByMediaId(mediaId);
    if (slide) eagerLoadSlideImage(slide);
  };

  /**
   * Immediately shows the swatch's variant image in the main gallery the moment a
   * swatch is selected, before the section fetch (network round-trip) completes.
   *
   * Variant images are rendered as slides (the selected one visible, the others
   * hidden), so we simply select the slide by its media id. slideshow.select()
   * reveals a hidden slide; its image is the same image_tag render as the gallery,
   * so it loads from cache (warmed on idle/hover) — instant for every variant,
   * including returning to the first/main image.
   *
   * @param {Event} event - The change event from a swatch input.
   */
  #handleSwatchChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const mediaId = input.dataset.optionMediaId;
    if (!mediaId) return;

    this.#showVariantSlide(mediaId);
  };

  /**
   * Collects every media id that belongs to a variant, read from the swatch inputs.
   * Media that is not in this set (detail shots not attached to any variant) stays
   * visible — that is what the server renders too.
   * @returns {Set<string>}
   */
  #variantMediaIds() {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    const ids = new Set();
    for (const input of scope.querySelectorAll('input[data-option-media-id]')) {
      const id = /** @type {HTMLInputElement} */ (input).dataset.optionMediaId;
      if (id) ids.add(id);
    }
    return ids;
  }

  /**
   * Shows the slide whose slide-id matches the given media id.
   *
   * The visibility swap is done directly rather than leaving it to slideshow.select():
   * select() only marks a hidden slide with [reveal] and never re-hides the slide that
   * was showing before, so on galleries with more than one visible slide the variant
   * images piled up and the whole gallery became swipeable. Animation is off on purpose
   * — the animated path in slideshow.js inserts a temporary empty <slideshow-slide>
   * placeholder that can be left behind as a blank swipeable slide.
   *
   * Guarded so an unknown id never blanks the gallery.
   * @param {string} mediaId
   */
  #showVariantSlide(mediaId) {
    const slideshow = this.slideshow;
    if (!slideshow || !mediaId) return;
    const slides = slideshow.refs?.slides;
    if (!slides || !slides.length) return;

    const target = slides.find((s) => s.getAttribute('slide-id') === String(mediaId));
    if (!target) return; // no matching slide — do nothing rather than blank the gallery

    eagerLoadSlideImage(target);

    const variantMediaIds = this.#variantMediaIds();

    for (const slide of slides) {
      if (slide === target) {
        slide.hidden = false;
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'false');
        continue;
      }

      // Only ever hide other variant images; media that belongs to no variant stays put.
      const id = slide.getAttribute('slide-id');
      if (id && variantMediaIds.has(id) && !slide.hasAttribute('hidden')) {
        slide.hidden = true;
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    slideshow.select({ id: mediaId }, undefined, { animate: false });
  }

  /**
   * Eager-loads every hidden variant slide image once idle, so switching to any
   * variant (and back to the first) is instant.
   */
  #preloadHiddenVariantSlides() {
    const slides = this.querySelectorAll('slideshow-slide[hidden][slide-id]');
    for (const slide of slides) eagerLoadSlideImage(slide);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Check if the selected variant has an image
    const variantResource = event.detail.resource;
    const hasVariantImage = variantResource?.featured_media?.preview_image?.src;

    // Remove any existing warning before replacing
    hideVariantImageWarning();

    this.replaceWith(newMediaGallery);

    // If the variant doesn't have an image, show a warning message after replacement
    if (!hasVariantImage) {
      // Use setTimeout to ensure the newMediaGallery is in the DOM
      // After replaceWith, we need to find the media-gallery element again in the DOM
      setTimeout(() => {
        const insertedMediaGallery = document.querySelector('media-gallery');
        if (insertedMediaGallery instanceof HTMLElement) {
          showVariantImageWarning(insertedMediaGallery);
        }
      }, 0);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
